/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/device/treeParser.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)    | Who               | Why                    | What Changed         |
 * |---|----------------------|-------------------|------------------------|----------------------|
 * | 1 | 2026-06-15 (Step 5)  | Claude Sonnet 4.6 | dumpTree (Step 5)      | Created from scratch |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx                  (dumpTree, A11yNode types)
 * - apps/studio/src/device/treeParser.test.ts (unit tests — parseUiautomatorXml)
 * - (Phase 6) packages/runner/src/resolver.ts (resolveTarget will import A11yNode)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - A11yBounds    (parsed bounds: left/top/right/bottom in pixels)
 * - A11yNode      (one UI node: attributes + children array)
 * - parseUiautomatorXml(xml: string): A11yNode[]  — pure, sync, testable in Node
 * - dumpTree(adb: Adb): Promise<A11yNode[]>       — runs shell command + calls parser
 *
 * CALLER → CALLEE MAP
 * --------------------
 * App.tsx → dumpTree(adb)
 *               → adb.subprocess.noneProtocol.spawnWaitText("uiautomator dump /dev/tty")
 *               → stripToXml(raw)
 *               → parseUiautomatorXml(xml)
 *                   → new XMLParser(...)
 *                   → xmlToNodes(parsedHierarchy.node)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. parseUiautomatorXml() is pure (no browser or Node APIs). It can be unit-tested
 *    in vitest with golden fixture files — no real device needed.
 * 2. `uiautomator dump /dev/tty` writes the XML directly to stdout. The command
 *    also prepends/appends a human-readable line: "UI hierchary dumped to: /dev/tty".
 *    stripToXml() extracts only the XML portion.
 * 3. bounds attribute format: "[x1,y1][x2,y2]" → parsed to { left, top, right, bottom }.
 * 4. resource-id, content-desc, and class may be empty strings — callers should treat
 *    empty string the same as missing when building IR candidates.
 * 5. All boolean attributes (clickable, enabled, etc.) are parsed from "true"/"false" strings.
 *
 * ISSUES FACED / KNOWN GOTCHAS
 * -----------------------------
 * - fast-xml-parser with ignoreAttributes: false returns attribute names as-is.
 *   Hyphens in attribute names (resource-id, content-desc, long-clickable) work fine
 *   when attributeNamePrefix is "".
 * - uiautomator dump can take 300–1500ms on real devices (see CLAUDE.md §7 A-3).
 *   Phase 3 replaces this with mq-device-server dex (30–80ms).
 * - The parsed node from fast-xml-parser may be a single object (not array) when
 *   there is only one child <node>. isArray callback forces array wrapping.
 *
 * =============================================================================
 */

import type { Adb } from "@yume-chan/adb";
import { XMLParser } from "fast-xml-parser";

/* ── Types ────────────────────────────────────────────────────────────────── */

export interface A11yBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface A11yNode {
  index: number;
  text: string;
  resourceId: string;       // maps to resource-id attribute
  className: string;        // maps to class attribute
  pkg: string;              // maps to package attribute
  contentDesc: string;      // maps to content-desc attribute
  bounds: A11yBounds;
  checkable: boolean;
  checked: boolean;
  clickable: boolean;
  enabled: boolean;
  focusable: boolean;
  focused: boolean;
  scrollable: boolean;
  longClickable: boolean;
  password: boolean;
  selected: boolean;
  children: A11yNode[];
}

/* ── Bounds parser ────────────────────────────────────────────────────────── */

function parseBounds(raw: string): A11yBounds {
  // Format: "[x1,y1][x2,y2]"
  const m = raw.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
  if (!m) return { left: 0, top: 0, right: 0, bottom: 0 };
  return {
    left: parseInt(m[1]!, 10),
    top: parseInt(m[2]!, 10),
    right: parseInt(m[3]!, 10),
    bottom: parseInt(m[4]!, 10),
  };
}

function parseBool(val: string | undefined): boolean {
  return val === "true";
}

function parseStr(val: string | undefined): string {
  return val ?? "";
}

/* ── Raw fast-xml-parser node shape ──────────────────────────────────────── */

interface RawXmlNode {
  index?: string;
  text?: string;
  "resource-id"?: string;
  class?: string;
  package?: string;
  "content-desc"?: string;
  bounds?: string;
  checkable?: string;
  checked?: string;
  clickable?: string;
  enabled?: string;
  focusable?: string;
  focused?: string;
  scrollable?: string;
  "long-clickable"?: string;
  password?: string;
  selected?: string;
}

interface RawXmlNodeWithChildren extends RawXmlNode {
  node?: RawXmlNodeWithChildren | RawXmlNodeWithChildren[];
}

/* ── Recursive converter ──────────────────────────────────────────────────── */

function xmlNodeToA11y(raw: RawXmlNodeWithChildren): A11yNode {
  const children: A11yNode[] = [];
  if (raw.node) {
    const childArr = Array.isArray(raw.node) ? raw.node : [raw.node];
    for (const c of childArr) {
      children.push(xmlNodeToA11y(c));
    }
  }

  return {
    index: parseInt(parseStr(raw.index), 10) || 0,
    text: parseStr(raw.text),
    resourceId: parseStr(raw["resource-id"]),
    className: parseStr(raw.class),
    pkg: parseStr(raw.package),
    contentDesc: parseStr(raw["content-desc"]),
    bounds: parseBounds(parseStr(raw.bounds)),
    checkable: parseBool(raw.checkable),
    checked: parseBool(raw.checked),
    clickable: parseBool(raw.clickable),
    enabled: parseBool(raw.enabled),
    focusable: parseBool(raw.focusable),
    focused: parseBool(raw.focused),
    scrollable: parseBool(raw.scrollable),
    longClickable: parseBool(raw["long-clickable"]),
    password: parseBool(raw.password),
    selected: parseBool(raw.selected),
    children,
  };
}

/* ── XML stripping helper ─────────────────────────────────────────────────── */

function stripToXml(raw: string): string {
  // Remove everything before the first '<' (e.g. "UI hierchary dumped to: /dev/tty\n")
  const idx = raw.indexOf("<?xml");
  if (idx > 0) return raw.slice(idx);
  const tagIdx = raw.indexOf("<hierarchy");
  if (tagIdx > 0) return raw.slice(tagIdx);
  return raw;
}

/* ── Public API ───────────────────────────────────────────────────────────── */

/**
 * Parse the XML string output of `uiautomator dump /dev/tty` into A11yNode[].
 * Pure function — no browser or Node.js APIs. Safe to unit-test anywhere.
 */
export function parseUiautomatorXml(xml: string): A11yNode[] {
  const cleaned = stripToXml(xml);

  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "",
    parseAttributeValue: false,
    // Always wrap <node> in an array so single-child elements are consistent
    isArray: (name) => name === "node",
  });

  const doc = parser.parse(cleaned) as {
    hierarchy?: { node?: RawXmlNodeWithChildren | RawXmlNodeWithChildren[] };
  };

  const roots = doc.hierarchy?.node;
  if (!roots) return [];

  const rootArr = Array.isArray(roots) ? roots : [roots];
  return rootArr.map(xmlNodeToA11y);
}

/**
 * Run `uiautomator dump /dev/tty` over ADB and return the parsed tree.
 * Latency: 300–1500ms. Used as the fallback when mq-device-server is unavailable.
 */
export async function dumpTree(adb: Adb): Promise<A11yNode[]> {
  const raw = await adb.subprocess.noneProtocol.spawnWaitText([
    "uiautomator",
    "dump",
    "/dev/tty",
  ]);
  return parseUiautomatorXml(raw);
}

/**
 * Fast tree dump via mq-device-server (30–80ms) with transparent fallback to
 * uiautomator dump (300–1500ms).
 *
 * Pass `mqdsSession` from the active MqdsSession; if it is null or inactive
 * the call falls back automatically — callers do not need to branch.
 */
export async function dumpTreeFast(
  adb: Adb,
  mqdsSession: { isActive: boolean; dumpTree: () => Promise<unknown[]> } | null,
): Promise<A11yNode[]> {
  if (mqdsSession?.isActive) {
    try {
      const nodes = await mqdsSession.dumpTree();
      // The JSON from the Kotlin server matches A11yNode shape directly —
      // cast is safe because TreeDumper.kt mirrors the treeParser.ts interface.
      return nodes as A11yNode[];
    } catch {
      // Fall through to uiautomator if the fast path errors mid-session
    }
  }
  return dumpTree(adb);
}
